/**
 * Doc mention helpers (Chunk 10a).
 *
 * A mention is an atomic inline tag inside a text block's HTML:
 *   <span data-doc-mention data-doc-id="…" contenteditable="false">📄 Title</span>
 *
 * contenteditable="false" makes the browser treat it as one unit — the caret
 * walks around it and Backspace removes it whole. It survives saves untouched
 * because block content is stored as raw HTML.
 */

const escapeHtml = (text = '') =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Emoji (or fallback 📄) — inline HTML can't host the app's rich icon renderer */
export const mentionIconText = (icon) => {
  if (typeof icon === 'string' && icon.trim()) return icon;
  if (icon && typeof icon === 'object' && icon.type === 'emoji' && icon.value) return icon.value;
  return '📄';
};

export const buildMentionHtml = (doc) =>
  `<span class="doc-mention" data-doc-mention data-doc-id="${escapeHtml(doc.id)}" contenteditable="false">` +
  `<span class="doc-mention__icon">${escapeHtml(mentionIconText(doc.icon))}</span>` +
  `<span class="doc-mention__title">${escapeHtml(doc.title || 'Untitled Doc')}</span>` +
  `</span>&nbsp;`;

/**
 * Rewrite every mention tag in a block's HTML with the doc's CURRENT title
 * and icon (10b: renaming a doc in the library updates every mention).
 *
 * `resolveDocMeta(docId)` returns { title, icon } or undefined; unknown ids
 * (deleted docs, still-loading list) are left exactly as stored. Returns the
 * original string when nothing changed so callers can cheap-compare.
 */
export const refreshMentionHtml = (html, resolveDocMeta) => {
  if (!html || !resolveDocMeta || !html.includes('data-doc-mention')) return html;

  const host = document.createElement('div');
  host.innerHTML = html;
  let changed = false;

  host.querySelectorAll('[data-doc-mention]').forEach((tag) => {
    const meta = resolveDocMeta(tag.getAttribute('data-doc-id'));
    if (!meta) return;

    const iconEl = tag.querySelector('.doc-mention__icon');
    const titleEl = tag.querySelector('.doc-mention__title');
    const freshIcon = mentionIconText(meta.icon);
    const freshTitle = meta.title || 'Untitled Doc';

    if (iconEl && iconEl.textContent !== freshIcon) {
      iconEl.textContent = freshIcon;
      changed = true;
    }
    if (titleEl && titleEl.textContent !== freshTitle) {
      titleEl.textContent = freshTitle;
      changed = true;
    }
  });

  return changed ? host.innerHTML : html;
};

/**
 * Replace the typed "/query" with the mention tag, in place.
 *
 * Works on the DOM directly (rather than string surgery on the block's HTML)
 * so everything else in the paragraph — bold runs, links, EARLIER mentions —
 * survives byte-for-byte. `slashNode` is the text node the caret sat in when
 * the slash menu opened (focus has since moved into the picker's search
 * input); the "/" and anything typed after it live in that node.
 * `queryLength` is how much was typed after the "/" before the picker took
 * over. Returns true when the tag was placed (caret ends up after it).
 */
export const replaceSlashWithMention = (el, slashNode, queryLength, mentionHtml) => {
  if (!slashNode || slashNode.nodeType !== Node.TEXT_NODE || !el.contains(slashNode)) {
    return false;
  }

  const text = slashNode.textContent;
  const slashIndex = text.lastIndexOf('/');
  if (slashIndex === -1) return false;
  const end = Math.min(slashIndex + 1 + queryLength, text.length);

  const range = document.createRange();
  range.setStart(slashNode, slashIndex);
  range.setEnd(slashNode, end);
  range.deleteContents();

  const fragment = range.createContextualFragment(mentionHtml);
  const lastNode = fragment.lastChild;
  range.insertNode(fragment);

  // Caret after the trailing space so the user can keep typing
  el.focus({ preventScroll: true });
  if (lastNode) {
    const after = document.createRange();
    after.setStartAfter(lastNode);
    after.collapse(true);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(after);
  }
  return true;
};
